import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Admins = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            console.log('Fetching admin data from database...');
            
            const response = await api.get('/direct/admins');
            console.log('Admins loaded from database:', response.data);
            setAdmins(response.data);
        } catch (error) {
            console.error('Error fetching admin data:', error);
            toast.error('Failed to load admin list');
            setAdmins([]);
        } finally {
            setLoading(false);
        }
    };

    const generateInviteLink = async () => {
        try {
            const response = await api.post('/admin/generate-invite');
            if (response.data && response.data.token) {
                const inviteLink = `${window.location.origin}/admin/invite-simple/${response.data.token}`;
                setGeneratedLink(inviteLink);
                setIsInviteModalOpen(true);
            } else {
                toast.error('Failed to generate invite token');
            }
        } catch (error) {
            console.error('Error generating invite link:', error);
            toast.error(error.response?.data?.message || 'Failed to generate invite link');
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedLink);
        setIsCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleStatusChange = async (adminId, newStatus) => {
        try {
            await api.patch(`/direct/admin/${adminId}/status`, { status: newStatus });
            toast.success(`Admin ${newStatus === 'active' ? 'approved' : 'rejected'} successfully`);
            fetchAdmins();
        } catch (error) {
            console.error('Error updating admin status:', error);
            toast.error('Failed to update admin status');
        }
    };

    const handleDeleteAdmin = async (adminId, adminName) => {
        if (window.confirm(`Are you sure you want to delete admin "${adminName}"? This action cannot be undone.`)) {
            try {
                await api.delete(`/direct/admin/${adminId}`);
                toast.success('Admin deleted successfully');
                fetchAdmins();
            } catch (error) {
                console.error('Error deleting admin:', error);
                toast.error('Failed to delete admin');
            }
        }
    };

    const refreshAdmins = () => {
        setLoading(true);
        fetchAdmins();
    };

    const getStatsData = () => {
        const totalAdmins = admins.length;
        const activeAdmins = admins.filter(admin => admin.status === 'active').length;
        const pendingAdmins = admins.filter(admin => admin.status === 'pending').length;
        const superAdmins = admins.filter(admin => admin.role === 'superadmin').length;

        return [
            { title: 'Total Admins', value: totalAdmins, icon: '👥', color: 'from-[#FF3366] to-[#FF6B4D]' },
            { title: 'Active', value: activeAdmins, icon: '✅', color: 'from-green-500 to-emerald-500' },
            { title: 'Pending', value: pendingAdmins, icon: '⏳', color: 'from-yellow-500 to-orange-500' },
            { title: 'Super Admins', value: superAdmins, icon: '⭐', color: 'from-purple-500 to-pink-500' },
        ];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3366]"></div>
            </div>
        );
    }

    return (
        <div className="relative z-10 p-4 lg:p-6">
            {/* Header */}
            <div className="mb-6 lg:mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D]">
                            Admin Management
                        </h1>
                        <p className="text-gray-400 mt-2 text-sm lg:text-base">Manage admin users and permissions</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                        <button 
                            onClick={refreshAdmins} 
                            className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:from-gray-500 hover:to-gray-600 transition-all duration-200 font-medium text-sm lg:text-base"
                        >
                            Refresh
                        </button>
                        <button 
                            onClick={generateInviteLink} 
                            className="px-4 lg:px-6 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-lg font-medium hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 shadow-lg transform hover:translate-y-[-1px] text-sm lg:text-base"
                        >
                            Invite Admin
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
                {getStatsData().map((stat, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl border border-[#FF3366]/10 p-4 lg:p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3 mb-3 lg:mb-4">
                                <div className="p-1.5 lg:p-2 rounded-lg bg-black/20">
                                    <span className="text-lg lg:text-xl">{stat.icon}</span>
                                </div>
                                <h3 className="text-xs lg:text-sm font-medium text-gray-300">{stat.title}</h3>
                            </div>
                            <div className="flex items-baseline">
                                <p className="text-2xl lg:text-3xl font-bold text-white">
                                    {stat.value}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Admins Table */}
            <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl overflow-hidden border border-[#FF3366]/10 shadow-lg">
                <div className="p-4 lg:p-6">
                    <h2 className="text-lg lg:text-xl font-semibold text-white mb-4">Admin Users</h2>
                    
                    {/* Mobile Card View */}
                    <div className="block lg:hidden space-y-4">
                        {admins.map((admin) => (
                            <div key={admin.id} className="bg-[#0A0A0A] rounded-lg border border-[#FF3366]/10 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] flex items-center justify-center text-white font-medium">
                                            {admin.name ? admin.name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                        <div>
                                            <h3 className="text-white font-medium text-sm">{admin.name}</h3>
                                            <p className="text-gray-400 text-xs">{admin.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            admin.role === 'superadmin' 
                                                ? 'bg-purple-500/10 text-purple-400 border border-purple-400/30'
                                                : 'bg-[#FF3366]/10 text-[#FF3366] border border-[#FF3366]/30'
                                        }`}>
                                            {admin.role}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                            admin.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-400/30' :
                                            admin.status === 'active' ? 'bg-green-500/10 text-green-400 border border-green-400/30' :
                                            'bg-red-500/10 text-red-400 border border-red-400/30'
                                        }`}>
                                            {admin.status}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                    <p className="text-gray-400 text-xs">
                                        Joined: {new Date(admin.created_at).toLocaleDateString()}
                                    </p>
                                    <div className="flex space-x-2">
                                        {admin.status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => handleStatusChange(admin.id, 'active')}
                                                    className="px-2 py-1 rounded text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-400/30 transition-all duration-200"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleStatusChange(admin.id, 'rejected')}
                                                    className="px-2 py-1 rounded text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-400/30 transition-all duration-200"
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        )}
                                        {admin.role !== 'superadmin' && (
                                            <button
                                                onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                                                className="px-2 py-1 rounded text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-400/30 transition-all duration-200"
                                                title="Delete Admin"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-sm text-gray-400 border-b border-[#FF3366]/10">
                                    <th className="pb-3 font-medium">ADMIN</th>
                                    <th className="pb-3 font-medium">EMAIL</th>
                                    <th className="pb-3 font-medium">ROLE</th>
                                    <th className="pb-3 font-medium">JOINED</th>
                                    <th className="pb-3 font-medium">STATUS</th>
                                    <th className="pb-3 font-medium">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {admins.map((admin) => (
                                    <tr key={admin.id} className="border-b border-[#FF3366]/5 hover:bg-[#FF3366]/5 transition-colors">
                                        <td className="py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] flex items-center justify-center text-white font-medium">
                                                    {admin.name ? admin.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                                <span className="text-white font-medium">{admin.name}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 text-gray-300">{admin.email}</td>
                                        <td className="py-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                admin.role === 'superadmin' 
                                                    ? 'bg-purple-500/10 text-purple-400 border border-purple-400/30'
                                                    : 'bg-[#FF3366]/10 text-[#FF3366] border border-[#FF3366]/30'
                                            }`}>
                                                {admin.role}
                                            </span>
                                        </td>
                                        <td className="py-4 text-gray-300">
                                            {new Date(admin.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="py-4">
                                            {admin.status === 'pending' ? (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-400/30">
                                                    Pending
                                                </span>
                                            ) : admin.status === 'active' ? (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-400/30">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-400/30">
                                                    Rejected
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-4">
                                            <div className="flex space-x-2">
                                                {admin.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleStatusChange(admin.id, 'active')}
                                                            className="px-3 py-1 rounded-lg text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-400/30 transition-all duration-200"
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleStatusChange(admin.id, 'rejected')}
                                                            className="px-3 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-400/30 transition-all duration-200"
                                                        >
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                                {admin.role !== 'superadmin' && (
                                                    <button
                                                        onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                                                        className="px-3 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-400/30 transition-all duration-200"
                                                        title="Delete Admin"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {admins.length === 0 && (
                        <div className="text-center py-8 lg:py-12">
                            <div className="text-4xl lg:text-6xl mb-4">👥</div>
                            <h3 className="text-lg lg:text-xl font-semibold text-white mb-2">No admins found</h3>
                            <p className="text-gray-400 mb-6 text-sm lg:text-base">Invite your first admin to get started.</p>
                            <button 
                                onClick={generateInviteLink} 
                                className="inline-flex items-center px-4 lg:px-6 py-2 lg:py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium text-sm lg:text-base"
                            >
                                Invite Admin
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Invite Modal */}
            {isInviteModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-[#151515] to-[#0A0A0A] rounded-xl p-6 lg:p-8 max-w-md w-full border border-[#FF3366]/20 shadow-2xl">
                        <h2 className="text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] mb-4">
                            Admin Invitation Link
                        </h2>
                        <p className="text-gray-300 mb-6 text-sm lg:text-base">
                            Share this link with the person you want to invite as an admin:
                        </p>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center mb-6 bg-[#0A0A0A] rounded-lg border border-[#FF3366]/20 overflow-hidden">
                            <input
                                type="text"
                                value={generatedLink}
                                readOnly
                                className="flex-1 p-3 bg-transparent border-0 text-white focus:outline-none text-sm lg:text-base"
                            />
                            <button
                                onClick={copyToClipboard}
                                className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white px-4 py-3 sm:rounded-r-lg font-medium hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 text-sm lg:text-base"
                            >
                                {isCopied ? 'Copied!' : 'Copy'}
                            </button>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsInviteModalOpen(false)}
                                className="px-4 lg:px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-all duration-200 text-sm lg:text-base"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admins; 